import React, { useEffect, useRef, useState } from "react"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { enqueueSnackbar } from "notistack"
import Skeleton from "react-loading-skeleton"
import notFound from "@/assets/img/not-found.png"
import deleteIcon from "@/assets/img/reservations/icons/delete.svg"
import Question from "@/components/modals/messages/Question"

function Orders() {
  const modalQuestionRef = useRef(null)

  const [state, setState] = useState({
    loading: true,
    data: [],
    deletingId: null,
  })

  useEffect(() => {
    loadOrders()
  }, [])

  const money = (val) => Number(val || 0).toFixed(2)

  const loadOrders = () => {
    setState((prev) => ({ ...prev, loading: true }))

    Api.get("aswo/orders")
      .then((res) => {
        setState((prev) => ({
          ...prev,
          loading: false,
          data: Array.isArray(res.data.items) ? res.data.items : [],
        }))
      })
      .catch(() => {
        setState((prev) => ({ ...prev, loading: false, data: [] }))
      })
  }

  const handleToggleRow = (rowId) => {
    document.querySelectorAll(".collapsable-table tr.active").forEach((el) => {
      el.classList.remove("active")
    })

    let rows = document.querySelectorAll(`tr[data-row="${rowId}"]`)

    for (let i = 0; i < rows.length; i++) {
      if (rows[i].classList.contains("active")) {
        rows[i].classList.remove("active")
      } else {
        rows[i].classList.add("active")
      }
    }
  }

  const handleDelete = (id) => {
    let modal = modalQuestionRef.current

    modal.open(id)
    modal.onSuccess(() => handleDestroy(id))
  }

  const handleDestroy = (id) => {
    setState((prev) => ({ ...prev, deletingId: id }))

    Api.post("aswo/delete-order", { id })
      .then(() => {
        enqueueSnackbar(trans("catalogue.orderDeleted"), { variant: "success" })
        setState((prev) => ({
          ...prev,
          data: prev.data.filter((o) => o.id !== id),
        }))
      })
      .catch(() => {
        enqueueSnackbar(trans("catalogue.orderDeleteFailed"), {
          variant: "error",
        })
      })
      .finally(() => {
        setState((prev) => ({ ...prev, deletingId: null }))
      })
  }

  const grandTotal = (order) =>
    Number(order.articles_total || 0) +
    Number(order.shipping_costs || 0) +
    Number(order.disposal_costs || 0) +
    Number(order.extra_charges || 0)

  return (
    <>
      <Question
        ref={modalQuestionRef}
        mainMessage={trans("catalogue.confirmDeleteOrder")}
        secondaryMessage={trans("catalogue.confirmDeleteOrderSub")}
      />

      <div className="pannel">
        {state.loading ? (
          <Skeleton height={45} count={10} />
        ) : state.data.length === 0 ? (
          <div className="not-found">
            <img src={notFound} alt="Not Found" />
            <p>{trans("catalogue.noOrders")}</p>
          </div>
        ) : (
          <div className="table-scroller">
            <table className="collapsable-table">
              <thead>
                <tr>
                  <th>{trans("catalogue.orderId")}</th>
                  <th>{trans("catalogue.orderDate")}</th>
                  <th>{trans("catalogue.orderedBy")}</th>
                  <th>{trans("catalogue.shippingMethod")}</th>
                  <th className="center">{trans("catalogue.articlesCount")}</th>
                  <th className="right">{trans("catalogue.total")}</th>
                  <th className="options"></th>
                </tr>
              </thead>
              <tbody>
                {state.data.map((order) => {
                  const rows = Array.isArray(order.rows) ? order.rows : []

                  return (
                    <React.Fragment key={"order-" + order.id}>
                      <tr
                        data-row={order.id}
                        onClick={() => handleToggleRow(order.id)}
                      >
                        <td>{order.order_id}</td>
                        <td>
                          {order.created_at
                            ? new Date(order.created_at).toLocaleString()
                            : "-"}
                        </td>
                        <td>
                          {order.user
                            ? `${order.user.name || ""} ${
                                order.user.lastname || ""
                              }`.trim()
                            : "-"}
                        </td>
                        <td>{order.ship_code || "-"}</td>
                        <td className="center">{rows.length}</td>
                        <td className="right">{money(grandTotal(order))}</td>
                        <td className="options">
                          <button
                            type="button"
                            className="button-remove"
                            title={trans("catalogue.deleteOrder")}
                            disabled={state.deletingId === order.id}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(order.id)
                            }}
                            style={{
                              width: "40px",
                              height: "40px",
                              padding: 0,
                              minWidth: "initial",
                              flexShrink: 0,
                            }}
                          >
                            <img src={deleteIcon} alt="delete" />
                          </button>
                        </td>
                      </tr>
                      <tr className="collapsable" data-row={order.id}>
                        <td colSpan="7">
                          <div className="pannel">
                            <table>
                              <thead>
                                <tr>
                                  <th>{trans("catalogue.number")}</th>
                                  <th>{trans("catalogue.name")}</th>
                                  <th>{trans("catalogue.originalNumber")}</th>
                                  <th>{trans("catalogue.brand")}</th>
                                  <th>{trans("catalogue.group")}</th>
                                  <th className="right">
                                    {trans("catalogue.price")}
                                  </th>
                                  <th className="center">
                                    {trans("catalogue.quantity")}
                                  </th>
                                  <th className="right">
                                    {trans("catalogue.total")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((row) => (
                                  <tr key={"order-row-" + row.id}>
                                    <td>{row.number}</td>
                                    <td>{row.name}</td>
                                    <td>
                                      {(row.original_number || "").trim() || "-"}
                                    </td>
                                    <td>{row.brand}</td>
                                    <td>{(row.group || "").trim() || "-"}</td>
                                    <td className="right">
                                      {money(row.price)}
                                    </td>
                                    <td className="center">{row.quantity}</td>
                                    <td className="right">
                                      {money(row.total_price)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

export default Orders
